import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { ReadService } from './read.service';
import {
  ISupplierAvailabilityException,
  ISupplierSchedule,
  SupplierAvailabilityStateEnum,
} from '../interfaces/supplier-schedule';
import { ISupplierScheduleFilter } from '../interfaces/supplier-schedule-filter';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import moment from 'moment';

@Injectable({ providedIn: 'root' })
export class SupplierScheduleService extends ReadService<
  ISupplierSchedule,
  ISupplierScheduleFilter
> {
  url = 'api/supplierSchedule';
  entityTitle = 'Supplier Schedule';

  constructor(http: HttpClient, handlersFactory: HttpResponsesHandlersFactory) {
    super(http, handlersFactory);
  }

  getClassifications(): Promise<any> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/getClassifications`),
      this.handlersFactory.getForReadAll(),
    );
  }

  getSuppliersAndJobs(filter: ISupplierScheduleFilter): Promise<any> {
    let params = this.constructHttpParams(filter);
    return this.handleRequest(
      this.http.get(`${this.url}/getSuppliersSchedule`, { params: params }),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  setSupplierAvailability(availabilityInfo: ISupplierSchedule) {
    return this.http
      .post<ISupplierSchedule>(`${this.url}/setSupplierAvailability`, availabilityInfo)
      .toPromise();
  }

  setSupplierAvailabilityDate(supplierId: number, exceptionDate: string, available: boolean) {
    return this.http
      .post<ISupplierSchedule>(`${this.url}/setSupplierAvailabilityDate`, {
        supplierId,
        exceptionDate,
        available,
      })
      .toPromise();
  }

  // *
  // supplier availability functions
  // *

  getDayAvailability(
    startDate: Date,
    monthAdd: number,
    dayAdd: number,
    availabilityExceptions: ISupplierAvailabilityException[],
  ) {
    let m = moment(startDate).add(monthAdd, 'M').add(dayAdd, 'd');
    let exceptAvail = this.getAvailableException(m.toDate(), availabilityExceptions);

    if (exceptAvail != null) return exceptAvail;
    else return null; // null equates to indeterminate or amber availability
  }

  getAvailableException(date: Date, availabilityExceptions: ISupplierAvailabilityException[]) {
    if (availabilityExceptions == null) return null;

    let except = this.getExceptionByDate(date, availabilityExceptions);
    if (except != undefined) {
      return except.availabilityState == SupplierAvailabilityStateEnum.available;
    } else {
      return null;
    }
  }

  getExceptionByDate(date: Date, availabilityExceptions: ISupplierAvailabilityException[]) {
    let except = availabilityExceptions.find(
      (e) =>
        new Date(e.exceptionDate).getDate() == date.getDate() &&
        new Date(e.exceptionDate).getMonth() == date.getMonth() &&
        new Date(e.exceptionDate).getFullYear() == date.getFullYear(),
    );

    return except;
  }

  getExceptionIndexByDate(
    date: Date,
    availabilityExceptions: ISupplierAvailabilityException[],
  ): number {
    let exceptIndex = availabilityExceptions.findIndex(
      (e) =>
        new Date(e.exceptionDate).getDate() == new Date(date).getDate() &&
        new Date(e.exceptionDate).getMonth() == new Date(date).getMonth() &&
        new Date(e.exceptionDate).getFullYear() == new Date(date).getFullYear(),
    );

    return exceptIndex;
  }

  getSupplierAvailability(filter: ISupplierScheduleFilter): Promise<ISupplierSchedule> {
    let params = this.constructHttpParams(filter);
    return this.handleRequest(
      this.http.get(`${this.url}/supplierAvailability`, { params: params }),
      this.handlersFactory.getForReadByFilter(),
    ).then(this.extractData);
  }

  getDayAvailabilityException(
    startDate: Date,
    monthAdd: number,
    dayAdd: number,
    availabilityExceptions: ISupplierAvailabilityException[],
  ): ISupplierAvailabilityException | null {
    let m = moment(startDate).add(monthAdd, 'M').add(dayAdd, 'd');
    return this.getExceptionByDate(m.toDate(), availabilityExceptions);
  }
}
